// src/components/compliance/CategoriesTab.tsx
//
// Manage the Compliance Category catalog. Backend seeds the SYSTEM
// categories on first read; system rows cannot be renamed or deleted
// (they can be deactivated).
//
// Gated by compliance.manage_categories for writes; compliance.view
// is enforced at the module level.

import { useState } from 'react';
import {
  useGetComplianceCategoriesQuery,
  useCreateComplianceCategoryMutation,
  useUpdateComplianceCategoryMutation,
  useDeleteComplianceCategoryMutation,
} from '../../api/complianceApi';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorState from '../common/ErrorState';
import Button from '../common/Button';
import ConfirmDialog from '../ui/ConfirmDialog';
import type { ComplianceCategory } from '../../types/api';
import { useHasCapability } from '../../services/centralContext';
import { messageFromComplianceError } from './complianceDisplay';

interface CategoriesTabProps {
  workspaceId: string;
}

export default function CategoriesTab({ workspaceId: _workspaceId }: CategoriesTabProps) {
  const canManage = useHasCapability('compliance', 'manage_categories');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<ComplianceCategory | null>(null);

  const { data, isLoading, error, refetch } = useGetComplianceCategoriesQuery({ includeInactive });
  const [deleteCategory, deleteState] = useDeleteComplianceCategoryMutation();
  const [errBanner, setErrBanner] = useState<string | null>(null);

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorState title="Could not load categories" message={messageFromComplianceError(error)} onRetry={refetch} />;

  const rows = data ?? [];

  return (
    <section className="space-y-4" data-testid="compliance-categories-tab">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <label className="text-sm text-gray-700 flex items-center gap-2">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
            data-testid="compliance-categories-include-inactive"
          />
          Include inactive
        </label>
        {canManage && (
          <Button onClick={() => setCreating(true)} data-testid="compliance-category-new-btn">
            New category
          </Button>
        )}
      </div>

      {errBanner && (
        <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {errBanner}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Key</th>
              <th className="px-4 py-2">Origin</th>
              <th className="px-4 py-2">Active</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row) => (
              <tr key={row.id} className="hover:bg-gray-50" data-testid={`compliance-category-row-${row.id}`}>
                <td className="px-4 py-2 text-gray-900">
                  <div className="flex items-center gap-2">
                    {row.icon && <span aria-hidden="true">{row.icon}</span>}
                    <span>{row.name}</span>
                  </div>
                  {row.description && <div className="text-xs text-gray-500 mt-0.5">{row.description}</div>}
                </td>
                <td className="px-4 py-2 text-gray-600 font-mono text-xs">{row.key}</td>
                <td className="px-4 py-2">
                  {row.isSystem ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 ring-1 ring-slate-200">System</span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 ring-1 ring-indigo-200">Custom</span>
                  )}
                </td>
                <td className="px-4 py-2">
                  {row.isActive ? (
                    <span className="text-emerald-700 text-xs">Active</span>
                  ) : (
                    <span className="text-gray-500 text-xs">Inactive</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right space-x-2">
                  {canManage && (
                    <>
                      <button
                        type="button"
                        onClick={() => setEditingId(row.id)}
                        className="text-xs text-indigo-700 hover:underline"
                        data-testid={`compliance-category-edit-${row.id}`}
                      >
                        Edit
                      </button>
                      {!row.isSystem && (
                        <button
                          type="button"
                          onClick={() => setDeleting(row)}
                          className="text-xs text-red-700 hover:underline"
                          data-testid={`compliance-category-delete-${row.id}`}
                        >
                          Delete
                        </button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="p-6 text-center text-sm text-gray-500">
                  No categories yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {(creating || editingId) && (
        <CategoryFormDialog
          category={editingId ? rows.find((r) => r.id === editingId) ?? null : null}
          onClose={() => {
            setCreating(false);
            setEditingId(null);
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          isOpen={true}
          title={`Delete "${deleting.name}"?`}
          message="This category will be soft-deleted. Requirements in it will keep their reference."
          confirmLabel={deleteState.isLoading ? 'Deleting…' : 'Delete'}
          variant="danger"
          onConfirm={async () => {
            setErrBanner(null);
            try {
              await deleteCategory(deleting.id).unwrap();
              setDeleting(null);
            } catch (e) {
              setErrBanner(messageFromComplianceError(e));
            }
          }}
          onCancel={() => setDeleting(null)}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// CategoryFormDialog
// ---------------------------------------------------------------------------

function CategoryFormDialog({
  category,
  onClose,
}: {
  category: ComplianceCategory | null;
  onClose: () => void;
}) {
  const editing = !!category;
  const [name, setName] = useState(category?.name ?? '');
  const [key, setKey] = useState(category?.key ?? '');
  const [description, setDescription] = useState(category?.description ?? '');
  const [icon, setIcon] = useState(category?.icon ?? '');
  const [color, setColor] = useState(category?.color ?? '');
  const [isActive, setIsActive] = useState(category?.isActive ?? true);
  const [error, setError] = useState<string | null>(null);

  const [create, createState] = useCreateComplianceCategoryMutation();
  const [update, updateState] = useUpdateComplianceCategoryMutation();
  const submitting = createState.isLoading || updateState.isLoading;

  const nameLocked = editing && !!category?.isSystem;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      if (editing && category) {
        await update({
          id: category.id,
          patch: {
            name: nameLocked ? undefined : name.trim(),
            description: description || null,
            icon: icon || null,
            color: color || null,
            isActive,
          },
        }).unwrap();
      } else {
        await create({
          key: key.trim(),
          name: name.trim(),
          description: description || null,
          icon: icon || null,
          color: color || null,
        }).unwrap();
      }
      onClose();
    } catch (err) {
      setError(messageFromComplianceError(err));
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="compliance-category-dialog-title">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl space-y-4"
        data-testid="compliance-category-form"
      >
        <h2 id="compliance-category-dialog-title" className="text-lg font-semibold text-gray-900">
          {editing ? 'Edit category' : 'New category'}
        </h2>

        {error && (
          <div role="alert" className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="cat-name">Name</label>
            <input
              id="cat-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={nameLocked}
              className="w-full rounded border-gray-300 text-sm disabled:bg-gray-100"
              required
            />
            {nameLocked && <p className="text-xs text-gray-500 mt-1">System categories cannot be renamed.</p>}
          </div>

          {!editing && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="cat-key">Key (unique, snake_case)</label>
              <input
                id="cat-key"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                className="w-full rounded border-gray-300 text-sm font-mono"
                required
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="cat-desc">Description</label>
            <textarea
              id="cat-desc"
              value={description ?? ''}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full rounded border-gray-300 text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="cat-icon">Icon (emoji, optional)</label>
              <input id="cat-icon" value={icon ?? ''} onChange={(e) => setIcon(e.target.value)} className="w-full rounded border-gray-300 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="cat-color">Color hex (optional)</label>
              <input id="cat-color" value={color ?? ''} onChange={(e) => setColor(e.target.value)} className="w-full rounded border-gray-300 text-sm" />
            </div>
          </div>

          {editing && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              Active
            </label>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : editing ? 'Save' : 'Create'}
          </Button>
        </div>
      </form>
    </div>
  );
}
